import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryParameter } from '@vp/common-ubik-utils';
import { DEBUG_ENABLED_FLAG_QUERY_PARAM } from '../../constants/queryParams';

const INITIAL_MOD_BOSS_LEVEL: string = 'Silent';

interface DebugModeContextValue {
  modBossLogLevel: string;
  setModBossLogLevel: (level: string) => void;
  debugModeEnabled: boolean;
  setDebugModeEnabled: (enabled: boolean) => void;
}

const DebugModeContext = createContext<DebugModeContextValue>({
  modBossLogLevel: INITIAL_MOD_BOSS_LEVEL,
  setModBossLogLevel: () => {},
  debugModeEnabled: false,
  setDebugModeEnabled: () => {},
});

export const DebugModeProvider = ({ children }: { children: ReactNode }) => {
  const debugEnabledQuery = useQueryParameter(DEBUG_ENABLED_FLAG_QUERY_PARAM);

  const [modBossLogLevel, setModBossLogLevel] = useState<string>(
    INITIAL_MOD_BOSS_LEVEL
  );
  // the reason we have a separate state for debug mode is to allow us to set it to false (i.e. when a user clicks the "Disable Debug Mode" button)
  // if it was just controlled by the query param, it would always be true if the query param is present
  const [debugModeEnabled, setDebugModeEnabled] = useState<boolean>(
    debugEnabledQuery === 'true' || typeof debugEnabledQuery === 'undefined' // show debug tools if user passed ?debug=true or just ?debug
  );

  const value = React.useMemo(
    () => ({
      modBossLogLevel,
      setModBossLogLevel,
      debugModeEnabled,
      setDebugModeEnabled,
    }),
    [modBossLogLevel, debugModeEnabled, setModBossLogLevel, setDebugModeEnabled]
  );

  return (
    <DebugModeContext.Provider value={value}>
      {children}
    </DebugModeContext.Provider>
  );
};
export const useDebugModeContext = () => {
  const context = useContext(DebugModeContext);
  if (context === undefined) {
    throw new Error(
      'useDebugModeContext must be used within a DebugModeProvider'
    );
  }
  return context;
};
